import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { getLeadBalance, LeadBalance, EMPTY_BALANCE } from '@/lib/leadBalance';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * CREDIT BALANCE BUS — one number, everywhere, always the server's.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * The header pill, the dashboard card, the membership page and the job detail
 * purchase gate all show the SAME balance. They must never drift apart, and
 * they must never show a number the browser invented.
 *
 * RULES:
 *  1. getLeadBalance() (src/lib/leadBalance.ts) is the ONLY reader. Nothing
 *     here caches a locally-computed total, and nothing ever does
 *     `balance - 1` after an unlock — it re-reads the server instead.
 *  2. Anything that changes the balance calls creditsChanged(). That includes
 *     single $29 purchases, credit-pack purchases, lead unlocks, membership
 *     purchases/renewals and cancellations.
 *  3. Subscribers re-read from the database. A stale render is impossible
 *     because the refresh is a round trip, not an optimistic local edit.
 *  4. Tab focus and cross-tab storage events also refresh, so a purchase
 *     completed in another tab (or a webhook-granted renewal that landed while
 *     the tab was in the background) shows up without a manual reload.
 */

const EVENT = 'giggone:credits-changed';
/** Written on every change so OTHER tabs get a storage event too. */
const SYNC_KEY = 'gg_credits_rev';

/** Announce that the server-side balance may have changed. */
export const creditsChanged = (reason = 'change') => {
  try {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { reason, at: Date.now() } }));
  } catch {
    /* non-browser context */
  }
  try {
    localStorage.setItem(SYNC_KEY, `${Date.now()}:${reason}`);
  } catch {
    /* private mode — same-tab refresh still works */
  }
};

/** Subscribe to balance-change announcements (same tab and other tabs). */
export const onCreditsChanged = (fn: () => void): (() => void) => {
  const local = () => fn();
  const cross = (e: StorageEvent) => {
    if (e.key === SYNC_KEY) fn();
  };
  window.addEventListener(EVENT, local as EventListener);
  window.addEventListener('storage', cross);
  return () => {
    window.removeEventListener(EVENT, local as EventListener);
    window.removeEventListener('storage', cross);
  };
};

/**
 * Live, server-authoritative balance for a user.
 *
 * Refreshes on: mount, every creditsChanged() announcement, tab focus, and
 * when the tab becomes visible again. Returns `refresh` so a caller can force
 * a re-read immediately after an action it performed itself.
 */
export const useLeadBalance = (userId?: string) => {
  const [balance, setBalance] = useState<LeadBalance>(EMPTY_BALANCE);
  const [loading, setLoading] = useState(!!userId);

  const refresh = useCallback(async () => {
    if (!userId) {
      setBalance(EMPTY_BALANCE);
      setLoading(false);
      return EMPTY_BALANCE;
    }
    try {
      const b = await getLeadBalance(userId);
      setBalance(b);
      return b;
    } catch {
      return EMPTY_BALANCE;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    let alive = true;
    const run = () => {
      if (alive) void refresh();
    };
    run();

    const offBus = onCreditsChanged(run);
    window.addEventListener('focus', run);
    const vis = () => {
      if (document.visibilityState === 'visible') run();
    };
    document.addEventListener('visibilitychange', vis);

    return () => {
      alive = false;
      offBus();
      window.removeEventListener('focus', run);
      document.removeEventListener('visibilitychange', vis);
    };
  }, [refresh]);

  return { balance, loading, refresh };
};

/**
 * ───────────────────────────────────────────────────────────────────────────
 * CREDIT TRANSACTION HISTORY
 * ───────────────────────────────────────────────────────────────────────────
 * Read straight from mkt_credit_ledger under RLS (`p_ledger_read` allows a user
 * to read their own rows and nothing else). Every row is written server-side,
 * inside the same transaction as the credit movement it describes:
 *
 *   pack_grant    +N  credit pack purchased
 *   single_grant  +1  $29 single lead credit purchased
 *   plan_grant    +N  membership payment settled (purchase or renewal)
 *   lead_spend    -1  a lead was unlocked
 *   plan_expired  -N  unused cycle credits forfeited at period end
 *   admin_grant   ±N  manual adjustment by an administrator
 */

export interface LedgerEntry {
  id: string;
  delta: number;
  kind: string;
  note: string | null;
  plan_code: string | null;
  listing_id: string | null;
  created_at: string;
}

export const fetchLedger = async (userId?: string, limit = 50): Promise<LedgerEntry[]> => {
  if (!userId) return [];
  const { data } = await supabase
    .from('mkt_credit_ledger')
    .select('id, delta, kind, note, plan_code, listing_id, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  return (data || []) as LedgerEntry[];
};

export interface PaymentEntry {
  id: string;
  kind: string;
  description: string | null;
  amount: number;
  status: string;
  external_payment_id: string | null;
  created_at: string;
}

/** Payment history for the membership page (RLS: own rows only). */
export const fetchPayments = async (userId?: string, limit = 25): Promise<PaymentEntry[]> => {
  if (!userId) return [];
  const { data } = await supabase
    .from('mkt_payments')
    .select('id, kind, description, amount, status, external_payment_id, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  return (data || []) as PaymentEntry[];
};

/** Human label for a ledger kind. */
export const ledgerLabel = (kind: string, lang: string): string => {
  const en: Record<string, string> = {
    pack_grant: 'Credit pack purchase',
    single_grant: 'Single lead credit ($29)',
    single_lead: 'Single lead credit ($29)',
    plan_grant: 'Membership credits',
    plan_renewal: 'Membership renewal',
    lead_spend: 'Lead unlocked',
    lead_claim: 'Lead unlocked',
    plan_expired: 'Unused cycle credits expired',
    admin_grant: 'Administrator adjustment',
  };
  const es: Record<string, string> = {
    pack_grant: 'Compra de paquete de créditos',
    single_grant: 'Crédito de lead individual ($29)',
    single_lead: 'Crédito de lead individual ($29)',
    plan_grant: 'Créditos de membresía',
    plan_renewal: 'Renovación de membresía',
    lead_spend: 'Lead desbloqueado',
    lead_claim: 'Lead desbloqueado',
    plan_expired: 'Créditos no usados expirados',
    admin_grant: 'Ajuste del administrador',
  };
  const map = lang === 'es' ? es : en;
  return map[kind] || kind.replace(/_/g, ' ');
};
